import { Label } from "@app/common/components/form/Label";
import { FilterIcon } from "@app/common/components/icons/FilterIcon";
import { Button } from "@app/componentLibrary/ui/button";
import {
    Dialog,
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogHeightModes,
    DialogTitle,
    DialogTrigger,
    DialogVerticalPositions,
} from "@app/componentLibrary/ui/dialog";
import { Flex } from "@app/componentLibrary/ui/flex";
import { Checkbox } from "@app/componentLibrary/ui/form/Checkbox";
import { ScrollArea, ScrollBar } from "@app/componentLibrary/ui/scroll-area";
import { Bold, ThemeTitle } from "@app/componentLibrary/ui/text";
import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { twMerge } from "tailwind-merge";

import { getTranslation } from "../hooks/useTranslation";
import { IFilterGroup } from "../types";
import { ControlledFormField } from "./form/ControlledFormField";

export interface IFilterForm {
    filters: { [key: string]: boolean | undefined };
}

export interface IFilterModalProps {
    filterGroups: IFilterGroup[];
    defaultFilters: { [key: string]: boolean | undefined };
    onSubmit: (filters: { [key: string]: boolean | undefined }) => void;
    triggerButtonClassName?: string;
}

export const FilterModalComponent = ({ filterGroups, defaultFilters, onSubmit, triggerButtonClassName }: IFilterModalProps) => {
    const {
        t,
        i18n: { language, languages },
    } = useTranslation(["common", "name"]);

    const [open, setOpen] = useState(false);

    const { control, handleSubmit, reset } = useForm<IFilterForm>({
        defaultValues: {
            filters: defaultFilters,
        },
    });

    // reset the form when the default filters change and the form opens
    useEffect(() => {
        if (open) {
            reset({ filters: defaultFilters });
        }
    }, [defaultFilters, open, reset]);

    const activeFilterCount = useMemo(() => Object.values(defaultFilters).filter((value) => value).length, [defaultFilters]);

    const handleSubmitInternal = (data: IFilterForm) => {
        onSubmit(data.filters);
        setOpen(false);
    };

    return (
        <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
                <Button
                    data-testid="filter-btn-icon"
                    size="icon"
                    variant="outline"
                    className={twMerge("mr-1 w-8 h-8 p-5 leading-3 bg-white hover:bg-gray-50 relative", triggerButtonClassName)}
                >
                    {activeFilterCount > 0 && (
                        <span className="absolute rounded-full left-full -translate-x-1/2 bottom-1/2 font-semibold text-xs -translate-y-1/2 text-white bg-primary h-5 w-5 pb-[1px] flex items-center justify-center">
                            {activeFilterCount}
                        </span>
                    )}
                    <FilterIcon className="h-4 w-4" />
                </Button>
            </DialogTrigger>
            <DialogContent verticalPosition={DialogVerticalPositions.Bottom} heightMode={DialogHeightModes.Fit} scrollContent={false}>
                <DialogHeader>
                    <DialogTitle>{t("filters")}</DialogTitle>
                    <DialogDescription hidden>{t("filters")}</DialogDescription>
                </DialogHeader>

                <form
                    data-testid="filter-modal"
                    onSubmit={handleSubmit(handleSubmitInternal)}
                    className="flex h-auto overflow-hidden flex-col mt-4"
                >
                    <ScrollArea className="">
                        <ScrollBar orientation="vertical" />
                        {/* Important:(there used to be code for packages, but it was removed, you can find it in the history) is temporarily disabled till we support packages, do not remove */}
                        {filterGroups
                            .filter((g) => !!g.filters.length)
                            .map((filterGroup) => (
                                <div className="p-6 px-4 pr-6 pt-0" key={filterGroup.id}>
                                    <ThemeTitle
                                        className="text-zinc-950 text-xs font-bold uppercase leading-tight tracking-tight mb-6"
                                        size="xs"
                                        color="slate"
                                    >
                                        {getTranslation(filterGroup.name, language, languages as string[])}
                                    </ThemeTitle>
                                    {filterGroup.filters.map((filter) => (
                                        <div key={filter.id} className="pb-2">
                                            <Label className="flex items-center text-neutral-900 text-base font-normal leading-none">
                                                <ControlledFormField
                                                    data-testid={`filter-item-${filter.id}`}
                                                    control={control}
                                                    component={Checkbox}
                                                    name={`filters.${filter.id}`}
                                                    className="ml-2 mr-3 w-8 h-8"
                                                />
                                                {getTranslation(filter.name, language, languages as string[])}
                                            </Label>
                                        </div>
                                    ))}
                                </div>
                            ))}
                    </ScrollArea>
                    <Flex
                        justifyContent="center"
                        alignItems="center"
                        className="py-4 mt-auto bg-gradient-to-b from-white/90 to-white   rounded-b-2xl"
                    >
                        <div className="w-full px-6">
                            <DialogClose asChild>
                                <Button
                                    data-testid="done-btn"
                                    type="submit"
                                    className="w-full rounded-lg text-base font-medium leading-tight"
                                    size="lg"
                                >
                                    <Bold color="white">{t("done")}</Bold>
                                </Button>
                            </DialogClose>
                        </div>
                    </Flex>
                </form>
            </DialogContent>
        </Dialog>
    );
};
